import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  JobDeliveryDto,
  JobDetailDto,
  UpdateJobDeliveryRequest,
} from "@/lib/jobs";
import { jobsApi, isJobContentLocked } from "@/lib/jobs";
import type { InvoiceDto, PaymentRecordSummaryDto } from "@/lib/invoices-payments";
import { getInvoice, listPayments } from "@/lib/invoices-payments";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { clientAddressesApi, clientsApi } from "@/lib/clients-accounts";
import { DeliveryMethodPicker } from "@/components/delivery-method-picker";
import { toast } from "@/hooks/use-toast";
import { NewClientAddressDialog } from "@/components/new-client-address-dialog";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Pencil, Check, X, Loader2, Send } from "lucide-react";

// ---------------------------------------------------------------------------
// JobDeliveryPymtTab
//
// Plan II — Task 4. Two-column form matching `Job Delivery and payment.png`.
// Left:  Delivery       (recipient / attn / phone / address / method / requested
//                        date / type checkboxes / instructions)
// Right: Invoice / Pymt (linked invoice, status, totals, payment records,
//                        balance)
//
// Adaptations from the plan sketch:
//   - The plan's field names (`deliveryRecipient`, `deliveryAddress` string,
//     `deliveryType` enum, etc.) do NOT exist on `JobDetailDto`. The real
//     shape is `JobDeliveryDto` (Plan III):
//        deliveryTo / deliveryAttn / deliveryPhone (text fields),
//        deliveryAddressId  / deliveryMethodId    (FK ids, read-only here —
//                                                  there is no lookup hook),
//        toBeDelivered / collectByCourier         (two booleans, NOT a single
//                                                  enum),
//        deliverySpecialInstructions.
//     `requestedDate` is read from `job.expectedDispatchDate` (read-only —
//     it's edited from the Details tab).
//   - There is no `useInvoicesForJob` hook nor a `jobId` filter on the invoice
//     list endpoint. The link is `job.generatedInvoiceId` (1:0..1). We fetch
//     the full `InvoiceDto` via `getInvoice` + payments via
//     `listPayments({ invoiceId })`. If the job has no generated invoice we
//     render a "No invoice yet" placeholder.
//   - There is no `useUpdateJob` / `useUpdateJobDelivery` hook; we use
//     `useMutation` directly with `jobsApi.updateDelivery` (same pattern as
//     JobClientTab → clientsApi.patch).
// ---------------------------------------------------------------------------

export interface JobDeliveryPymtTabProps {
  job: JobDetailDto;
}

interface Draft {
  deliveryTo: string;
  deliveryAttn: string;
  deliveryPhone: string;
  deliveryAddressId: number | null;
  deliveryMethodId: number | null;
  deliverySpecialInstructions: string;
  toBeDelivered: boolean;
  collectByCourier: boolean;
}

function draftFromJob(
  job: JobDetailDto,
  clientDefault: { deliveryAddressId: number | null } | null = null,
): Draft {
  const d: JobDeliveryDto | null = job.delivery;
  return {
    deliveryTo: d?.deliveryTo ?? "",
    deliveryAttn: d?.deliveryAttn ?? "",
    deliveryPhone: d?.deliveryPhone ?? "",
    deliveryAddressId: d?.deliveryAddressId ?? clientDefault?.deliveryAddressId ?? null,
    deliveryMethodId: d?.deliveryMethodId ?? null,
    deliverySpecialInstructions: d?.deliverySpecialInstructions ?? "",
    toBeDelivered: d?.toBeDelivered ?? false,
    collectByCourier: d?.collectByCourier ?? false,
  };
}

function buildUpdateRequest(
  job: JobDetailDto,
  draft: Draft,
): UpdateJobDeliveryRequest {
  // Non-null assertion safe: callers only invoke this from inside edit mode,
  // and entering edit mode is gated on `job.rowVersionBase64 != null` via the
  // Edit-button tooltip below. Asserting at the call site makes the contract
  // explicit instead of silently bypassing optimistic concurrency with `?? ""`.
  return {
    deliveryAddressId: draft.deliveryAddressId,
    deliveryMethodId: draft.deliveryMethodId,
    deliveryTo: draft.deliveryTo || null,
    deliveryAttn: draft.deliveryAttn || null,
    deliveryPhone: draft.deliveryPhone || null,
    deliverySpecialInstructions: draft.deliverySpecialInstructions || null,
    toBeDelivered: draft.toBeDelivered,
    collectByCourier: draft.collectByCourier,
    rowVersionBase64: job.rowVersionBase64!,
  };
}

export function JobDeliveryPymtTab({ job }: JobDeliveryPymtTabProps) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);

  const clientQuery = useQuery({
    queryKey: ["client", job.clientId],
    queryFn: () => clientsApi.get(job.clientId!),
    enabled: job.clientId != null,
  });

  const clientDefault = clientQuery.data
    ? { deliveryAddressId: clientQuery.data.deliveryAddressId }
    : null;

  const [draft, setDraft] = useState<Draft>(() => draftFromJob(job, clientDefault));

  // Two gates:
  //   locked   — job status is read-only (backend 409); never show edit mode.
  //   rowVersion must be present (optimistic-concurrency guard).
  const locked = isJobContentLocked(job.currentStatusCode);
  const editDisabledReason = locked
    ? `This job is ${job.currentStatusCode} and is read-only.`
    : job.rowVersionBase64 == null
      ? "Concurrency token missing — refresh the page to enable editing."
      : null;

  // Sync draft if the underlying job changes (e.g. after a status change
  // refreshes the parent query). Gate on !editing so an in-flight user
  // edit isn't clobbered by a refetch.
  useEffect(() => {
    if (!editing) {
      setDraft(draftFromJob(
        job,
        clientQuery.data ? { deliveryAddressId: clientQuery.data.deliveryAddressId } : null,
      ));
    }
  }, [job, clientQuery.data, editing]);

  const updateMut = useMutation({
    mutationFn: (body: UpdateJobDeliveryRequest) =>
      jobsApi.updateDelivery(job.jobId, body),
    onSuccess: () => {
      toast.success("Delivery updated");
      void qc.invalidateQueries({ queryKey: ["job", job.jobId] });
      setEditing(false);
    },
    onError: (err: Error) => toast.error(`Update failed: ${err.message}`),
  });

  const handleSave = () => {
    updateMut.mutate(buildUpdateRequest(job, draft));
  };

  const handleCancel = () => {
    setDraft(draftFromJob(job, clientDefault));
    setEditing(false);
  };

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <DeliverySection
        job={job}
        draft={draft}
        setDraft={setDraft}
        editing={editing}
        onAddAddressClick={() => setAddressDialogOpen(true)}
        setEditing={setEditing}
        onSave={handleSave}
        onCancel={handleCancel}
        isPending={updateMut.isPending}
        editDisabledReason={editDisabledReason}
      />
      <InvoicePaymentSection invoiceId={job.generatedInvoiceId} />

      {job.clientId != null && (
        <NewClientAddressDialog
          clientId={job.clientId}
          open={addressDialogOpen}
          onOpenChange={setAddressDialogOpen}
          onCreated={(newId) => setDraft((d) => ({ ...d, deliveryAddressId: newId }))}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delivery section (left column)
// ---------------------------------------------------------------------------

function DeliverySection({
  job,
  draft,
  setDraft,
  editing,
  onAddAddressClick,
  setEditing,
  onSave,
  onCancel,
  isPending,
  editDisabledReason,
}: {
  job: JobDetailDto;
  draft: Draft;
  setDraft: (d: Draft) => void;
  editing: boolean;
  onAddAddressClick: () => void;
  setEditing: (val: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
  isPending: boolean;
  editDisabledReason: string | null;
}) {
  return (
    <section className="bg-white rounded-xl border border-slate-300 shadow-sm p-5">
      <div className="mb-4 flex min-h-[32px] items-center justify-between border-b border-slate-100 pb-2">
        <h3 className="text-base font-bold text-[#0f3453]">
          Delivery
        </h3>
        {editing ? (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={isPending} className="h-7 w-7 border-transparent bg-transparent text-slate-500 hover:bg-transparent shadow-none" aria-label="Cancel">
              <X className="h-4 w-4" />
            </Button>
            <Button size="sm" onClick={onSave} disabled={isPending} className="h-7 w-7 border-transparent bg-info/15 text-primary hover:bg-info/25 shadow-none" aria-label="Save">
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            </Button>
          </div>
        ) : editDisabledReason == null ? (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="h-6 w-6 border-transparent bg-transparent text-primary hover:bg-transparent hover:text-primary/80 shadow-none" aria-label="Edit">
            <Pencil className="h-4 w-4" />
          </Button>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span role="button" aria-disabled="true" tabIndex={0}>
                  <Button variant="ghost" size="sm" disabled className="h-6 w-6 border-transparent bg-transparent text-slate-400 hover:bg-transparent shadow-none">
                    <Pencil className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {editDisabledReason}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className="space-y-4">
      <div>
        <Label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5 block">Delivery method</Label>
        <DeliveryMethodPicker
          value={draft.deliveryMethodId}
          onChange={(v) => setDraft({ ...draft, deliveryMethodId: v })}
          disabled={!editing}
        />
      </div>
      <Field
        label="Recipient (Deliver to)"
        value={draft.deliveryTo}
        disabled={!editing}
        onChange={(v) => setDraft({ ...draft, deliveryTo: v })}
      />
      <Field
        label="Attention"
        value={draft.deliveryAttn}
        disabled={!editing}
        onChange={(v) => setDraft({ ...draft, deliveryAttn: v })}
      />
      <Field
        label="Phone"
        value={draft.deliveryPhone}
        disabled={!editing}
        onChange={(v) => setDraft({ ...draft, deliveryPhone: v })}
      />
      <AddressDropdown
        clientId={job.clientId}
        value={draft.deliveryAddressId}
        disabled={!editing}
        onChange={(v) => setDraft({ ...draft, deliveryAddressId: v })}
        onAddNewClick={editing ? onAddAddressClick : undefined}
      />

      <div>
        <Label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5 block">Requested date</Label>
        <div className="text-sm">
          {job.expectedDispatchDate ? formatDate(job.expectedDispatchDate) : "—"}
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1 block">Delivery type</Label>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="delivery-to-be-delivered"
              checked={draft.toBeDelivered}
              disabled={!editing}
              onCheckedChange={(v) =>
                setDraft({ ...draft, toBeDelivered: v === true })
              }
            />
            <Label htmlFor="delivery-to-be-delivered" className="text-sm">
              To be delivered
            </Label>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="delivery-collect-by-courier"
              checked={draft.collectByCourier}
              disabled={!editing}
              onCheckedChange={(v) =>
                setDraft({ ...draft, collectByCourier: v === true })
              }
            />
            <Label htmlFor="delivery-collect-by-courier" className="text-sm">
              Collect by courier
            </Label>
          </div>
        </div>
      </div>
      <div>
        <Label
          htmlFor="delivery-special-instructions"
          className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5 block"
        >
          Special instructions
        </Label>
        <Textarea
          id="delivery-special-instructions"
          value={draft.deliverySpecialInstructions}
          onChange={(e) =>
            setDraft({ ...draft, deliverySpecialInstructions: e.target.value })
          }
          disabled={!editing}
          rows={3}
        />
      </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Invoice / Payment section (right column)
//
// 1:0..1 link via `job.generatedInvoiceId`. Payments fetched separately
// from `listPayments({ invoiceId })`.
// ---------------------------------------------------------------------------

function InvoicePaymentSection({ invoiceId }: { invoiceId: number | null }) {
  if (invoiceId == null) {
    return (
      <section className="bg-white rounded-xl border border-slate-300 shadow-sm p-5">
        <div className="mb-4 flex min-h-[32px] items-center border-b border-slate-100 pb-2">
          <h3 className="text-base font-bold text-[#0f3453]">
            Invoice / Payment
          </h3>
        </div>
        <p className="text-sm text-muted-foreground">No invoice yet</p>
      </section>
    );
  }
  return <InvoicePaymentBody invoiceId={invoiceId} />;
}

function InvoicePaymentBody({ invoiceId }: { invoiceId: number }) {
  const invoiceQuery = useQuery<InvoiceDto>({
    queryKey: ["invoice", invoiceId],
    queryFn: () => getInvoice(invoiceId),
  });

  const paymentsQuery = useQuery({
    queryKey: ["payments", { invoiceId }],
    queryFn: () => listPayments({ invoiceId }),
  });

  return (
    <section className="bg-white rounded-xl border border-slate-300 shadow-sm p-5">
      <div className="mb-4 flex min-h-[32px] items-center border-b border-slate-100 pb-2">
        <h3 className="text-base font-bold text-[#0f3453]">
          Invoice / Payment
        </h3>
      </div>
      <div className="space-y-4">

      {invoiceQuery.isLoading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-4 w-32 rounded bg-muted" />
          <div className="h-4 w-48 rounded bg-muted" />
        </div>
      ) : invoiceQuery.isError || !invoiceQuery.data ? (
        <p className="text-sm text-destructive">Failed to load invoice.</p>
      ) : (
        <InvoiceCard
          invoice={invoiceQuery.data}
          payments={paymentsQuery.data?.items ?? []}
          paymentsLoading={paymentsQuery.isLoading}
        />
      )}
      </div>
    </section>
  );
}

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

function InvoiceCard({
  invoice,
  payments,
  paymentsLoading,
}: {
  invoice: InvoiceDto;
  payments: PaymentRecordSummaryDto[];
  paymentsLoading: boolean;
}) {
  return (
    <div className="rounded-xl border bg-white shadow-sm overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-slate-50 border-b px-4 py-3 flex items-center justify-between">
        <Link
          to={`/invoices/${invoice.invoiceId}`}
          className="text-sm font-semibold text-primary hover:underline"
        >
          {invoice.invoiceReferenceNo}
        </Link>
        <Badge 
          variant={invoice.invoiceStatusCode === "Sent" ? "default" : "outline"}
          className={cn(
            invoice.invoiceStatusCode === "Sent" 
              ? "bg-emerald-500 hover:bg-emerald-600 text-white border-transparent gap-1.5 px-2.5 py-0.5" 
              : "bg-white"
          )}
        >
          {invoice.invoiceStatusCode === "Sent" && <Send className="h-3 w-3" />}
          {invoice.invoiceStatusCode}
        </Badge>
      </div>

      {/* Balances */}
      <div className="p-4 bg-white grid grid-cols-2 gap-4 border-b">
        <div className="flex flex-col">
           <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Total (inc GST)</span>
           <span className="text-sm font-medium text-slate-900">{fmt.format(invoice.invoiceTotalIncGst)}</span>
        </div>
        <div className="flex flex-col text-right">
           <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Balance</span>
           <span className="text-sm font-bold text-slate-900">{fmt.format(invoice.balanceOutstanding)}</span>
        </div>
      </div>

      {/* Details breakdown */}
      <div className="p-4 bg-slate-50/50">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <dt className="text-muted-foreground">Total ex GST</dt>
          <dd className="text-right font-medium text-slate-700">{fmt.format(invoice.invoiceTotalExGst)}</dd>
          
          <dt className="text-muted-foreground">GST</dt>
          <dd className="text-right font-medium text-slate-700">{fmt.format(invoice.gst)}</dd>
          
          <dt className="text-muted-foreground">Paid</dt>
          <dd className="text-right font-medium text-slate-700">{fmt.format(invoice.amountPaid)}</dd>
        </dl>
      </div>

      {/* Payments */}
      <div className="p-4 border-t bg-white">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Payment Records
        </h4>
        {paymentsLoading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : payments.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No payments yet</p>
        ) : (
          <ul className="space-y-2 text-xs">
            {payments.map((p) => (
              <li
                key={p.paymentRecordId}
                className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border border-slate-100"
              >
                <div className="flex flex-col gap-0.5">
                  <Link
                    to={`/payments/${p.paymentRecordId}`}
                    className="font-semibold text-primary hover:underline"
                  >
                    #{p.paymentRecordId}
                  </Link>
                  <span className="text-[10px] text-muted-foreground">
                    {formatDate(p.transactionDate)}
                  </span>
                </div>
                <span className="font-medium text-slate-900">{fmt.format(p.tranAmount)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddressDropdown — delivery address picker populated from clientAddressesApi
// ---------------------------------------------------------------------------

function AddressDropdown({
  clientId,
  value,
  disabled,
  onChange,
  onAddNewClick,
}: {
  clientId: number | null;
  value: number | null;
  disabled?: boolean;
  onChange: (id: number | null) => void;
  onAddNewClick?: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["client-addresses", clientId],
    queryFn: () => clientAddressesApi.list(clientId!),
    enabled: clientId != null,
  });
  const items = data ?? [];
  return (
    <div>
      <Label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5 block">Delivery address</Label>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Select
            value={value != null ? String(value) : "__none__"}
            onValueChange={(v) => onChange(v === "__none__" ? null : Number(v))}
            disabled={disabled || clientId == null}
          >
            <SelectTrigger data-testid="address-dropdown">
              <SelectValue
                placeholder={
                  clientId == null
                    ? "Select a client first"
                    : isLoading
                      ? "Loading…"
                      : "Select address"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">(none)</SelectItem>
              {items.map((a) => (
                <SelectItem key={a.id} value={String(a.id)}>
                  {a.formattedAddress ?? a.address}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {onAddNewClick && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAddNewClick}
            disabled={disabled || clientId == null}
          >
            + Add
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field — labelled text input
// ---------------------------------------------------------------------------

function Field({
  label,
  value,
  onChange,
  disabled,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  type?: string;
}) {
  const id = `delivery-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div>
      <Label htmlFor={id} className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5 block">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}
